import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useToastStore } from "@/components";
import { careplanQueryOptions } from "@/domains/careplan/factories/careplan-query-options";
import cartLinesUpdateMutation from "@/domains/careplan/models/graphql/cart-lines-update-mutation.graphql?raw";
import {
  CartLineNetworkError,
  CartLineOutOfStockError,
  CartLineValidationError,
  getCartLineErrorMessage,
  STOCK_WARNING_CODES,
} from "@/domains/careplan/mutations/cart-line-errors";
import { storefrontApi } from "@/modules/network";
import type {
  CartLinesUpdateMutation,
  CartLinesUpdateMutationVariables,
} from "@/types/storefront-api.gen";

export const useUpdateCareplanCartLine = () => {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((state) => {
    return state.pushToast;
  });

  return useMutation({
    mutationFn: updateCareplanCartLine,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: careplanQueryOptions.cart.queryKey,
      });
    },
    onError: async (error) => {
      await pushToast({
        type: "info",
        title: "Update Cart Failed",
        message: getCartLineErrorMessage(error),
      });
    },
  });
};

export const updateCareplanCartLine = async (variables: CartLinesUpdateMutationVariables) => {
  let response: Awaited<ReturnType<typeof storefrontApi.request<CartLinesUpdateMutation>>>;

  try {
    response = await storefrontApi.request<CartLinesUpdateMutation>(cartLinesUpdateMutation, {
      variables,
    });
  } catch (error) {
    throw new CartLineNetworkError("Failed to reach Shopify while updating cart", {
      cause: error,
    });
  }

  const requestedLine = Array.isArray(variables.lines) ? variables.lines[0] : variables.lines;

  const stockWarning = response.data?.cartLinesUpdate?.warnings.find((warning) => {
    return STOCK_WARNING_CODES.has(warning.code) && warning.target === requestedLine.id;
  });

  if (stockWarning) {
    throw new CartLineOutOfStockError(stockWarning.message);
  }

  const cartLines = response.data?.cartLinesUpdate?.cart?.lines.edges ?? [];
  const updatedLine = cartLines.find(({ node }) => {
    return node.id === requestedLine.id;
  })?.node;

  const updateApplied =
    requestedLine.quantity === 0
      ? updatedLine === undefined
      : updatedLine?.quantity === requestedLine.quantity;

  if (!updateApplied) {
    const shopifyMessage = response.data?.cartLinesUpdate?.userErrors[0]?.message;

    if (shopifyMessage) {
      throw new CartLineValidationError(shopifyMessage);
    }

    throw new CartLineOutOfStockError(
      "Could not update cart line — the requested quantity was not applied",
    );
  }

  return response.data;
};
